import { Injectable, signal } from '@angular/core';

export type OverlayType = 'loading' | 'success' | 'error' | 'confirm';

export interface ConfirmOptions {
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
}

export interface OverlayState {
  type: OverlayType;
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
}

@Injectable({ providedIn: 'root' })
export class GlobalOverlayService {
  readonly state = signal<OverlayState | null>(null);

  private confirmResolver?: (confirmed: boolean) => void;

  showLoading(message = 'กรุณารอสักครู่...'): void {
    this.finishPendingConfirm(false);
    this.state.set({
      type: 'loading',
      title: 'กำลังดำเนินการ',
      message,
      confirmText: '',
      cancelText: '',
    });
  }

  hideLoading(): void {
    if (this.state()?.type === 'loading') {
      this.state.set(null);
    }
  }

  success(title = 'สำเร็จ', message = ''): void {
    this.openMessage('success', title, message);
  }

  error(title = 'เกิดข้อผิดพลาด', message = 'กรุณาลองใหม่อีกครั้ง'): void {
    this.openMessage('error', title, message);
  }

  confirm(options: ConfirmOptions = {}): Promise<boolean> {
    this.finishPendingConfirm(false);
    this.state.set({
      type: 'confirm',
      title: options.title ?? 'ยืนยันการดำเนินการ',
      message: options.message ?? 'คุณต้องการดำเนินการต่อหรือไม่',
      confirmText: options.confirmText ?? 'ยืนยัน',
      cancelText: options.cancelText ?? 'ยกเลิก',
    });

    return new Promise<boolean>((resolve) => {
      this.confirmResolver = resolve;
    });
  }

  accept(): void {
    if (this.state()?.type === 'confirm') {
      this.finishPendingConfirm(true);
      return;
    }
    this.close();
  }

  cancel(): void {
    if (this.state()?.type === 'loading') return;
    this.finishPendingConfirm(false);
    this.state.set(null);
  }

  close(): void {
    this.finishPendingConfirm(false);
    this.state.set(null);
  }

  private openMessage(type: 'success' | 'error', title: string, message: string): void {
    this.finishPendingConfirm(false);
    this.state.set({ type, title, message, confirmText: 'ตกลง', cancelText: '' });
  }

  private finishPendingConfirm(result: boolean): void {
    this.confirmResolver?.(result);
    this.confirmResolver = undefined;
    if (this.state()?.type === 'confirm') {
      this.state.set(null);
    }
  }
}
